import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Brain, Zap, Bell, Sparkles, GitBranch, FileText, Cpu, TrendingUp, CheckCircle } from 'lucide-react';
import '@/styles/print.css';

const ESCENARIOS_RAPIDOS = [
  {
    icono: '⚡',
    label: 'Adelantar cobro',
    nombre: 'Adelantar cobro a cliente',
    descripcion: '¿Qué pasa si cobro antes a mis clientes más importantes?',
    tipo: 'adelantar_pago',
  },
  {
    icono: '🔄',
    label: 'Retrasar pago',
    nombre: 'Retrasar pago a proveedor',
    descripcion: '¿Qué pasa si negocio más días de crédito con mis proveedores?',
    tipo: 'retrasar_cobro',
  },
  {
    icono: '📈',
    label: 'Venta extra',
    nombre: 'Ingreso extraordinario',
    descripcion: '¿Qué pasa si cierro una venta adicional este mes?',
    tipo: 'agregar_transaccion',
  },
  {
    icono: '✂️',
    label: 'Reducir gasto',
    nombre: 'Reducir gasto operativo',
    descripcion: '¿Qué pasa si reduzco un gasto fijo este mes?',
    tipo: 'ajustar_monto',
  },
];

const AdvancedFeatures = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [reconResult, setReconResult] = useState(null);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [scenarioDialog, setScenarioDialog] = useState(false);
  const [optimizationDialog, setOptimizationDialog] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  const [optimizationConfig, setOptimizationConfig] = useState({
    generaciones: 20,
    poblacion: 30,
    max_retraso_dias: 30,
    max_adelanto_dias: 15
  });
  const [scenarioForm, setScenarioForm] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'adelantar_pago',
    monto: '',
    fecha: ''
  });

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai/predictive-analysis');
      if (res.data.status === 'insufficient_data') {
        toast.warning(res.data.message);
      } else {
        setAnalysis(res.data);
        toast.success('Análisis completado');
      }
    } catch (error) {
      toast.error('Error en análisis predictivo');
    } finally {
      setLoading(false);
    }
  };

  const runAutoRecon = async () => {
    try {
      const res = await api.post('/reconciliation/auto-reconcile-batch?min_score=85');
      setReconResult(res.data);
      toast.success(`${res.data.reconciled} movimientos conciliados`);
    } catch (error) {
      toast.error('Error en conciliación automática');
    }
  };

  const checkAlerts = async () => {
    try {
      const res = await api.post('/alerts/check-and-send');
      toast.success(`${res.data.alerts_sent} alertas enviadas`);
    } catch (error) {
      toast.error('Error verificando alertas');
    }
  };

  const createScenario = async () => {
    try {
      const modificacion = {
        tipo: scenarioForm.tipo,
        nuevo_monto: scenarioForm.monto ? parseFloat(scenarioForm.monto) : undefined,
        nueva_fecha: scenarioForm.fecha || undefined,
        razon: 'Simulación desde UI'
      };

      const res = await api.post('/scenarios/create', {
        nombre: scenarioForm.nombre,
        descripcion: scenarioForm.descripcion,
        modificaciones: [modificacion]
      });

      setScenarioResult(res.data);
      toast.success('Escenario creado');
      setScenarioDialog(false);
      setScenarioForm({ nombre: '', descripcion: '', tipo: 'adelantar_pago', monto: '', fecha: '' });
    } catch (error) {
      toast.error('Error creando escenario');
    }
  };

  const runGeneticOptimization = async () => {
    setLoading(true);
    try {
      toast.info('Ejecutando optimización genética... (esto puede tomar 30-60 segundos)');

      const res = await api.post('/optimize/genetic', {
        objetivos: {
          maximizar_liquidez: true,
          minimizar_costos: true,
          evitar_crisis: true
        },
        restricciones: {
          max_retraso_dias: optimizationConfig.max_retraso_dias,
          max_adelanto_dias: optimizationConfig.max_adelanto_dias,
          min_saldo: 50000
        },
        parametros: {
          generaciones: optimizationConfig.generaciones,
          poblacion: optimizationConfig.poblacion,
          prob_mutacion: 0.2
        }
      });

      const data = res.data;
      if (data.status === 'no_improvement') {
        setOptimizationResult({ _no_improvement: true });
        setOptimizationDialog(false);
        return;
      }
      const hasSolutions = data?.top_5_soluciones?.length > 0 && data?.generaciones > 0;
      if (!hasSolutions) {
        setOptimizationResult({ _insufficient_data: true });
      } else {
        setOptimizationResult(data);
        toast.success('¡Optimización completada! Encontradas mejores soluciones');
      }
      setOptimizationDialog(false);
    } catch (error) {
      const isDataError = error.response?.status === 400 || error.response?.status === 422;
      if (isDataError) {
        setOptimizationResult({ _insufficient_data: true });
        setOptimizationDialog(false);
      } else {
        toast.error('Error en optimización genética');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    try {
      const res = await api.get('/ai/export-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `IA_Ejecutiva_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      toast.success('PDF generado correctamente');
    } catch (error) {
      toast.error('Error generando PDF');
    }
  };

  const applyOptimization = async (optimizationId) => {
    try {
      const res = await api.post(`/optimize/apply/${optimizationId}`);
      const applied = res.data.modificaciones_aplicadas || 0;
      const mejora = res.data.mejora_esperada || 0;
      setAppliedResult(res.data);
      setOptimizationResult(null);
      if (applied > 0) {
        toast.success(`${applied} modificaciones aplicadas. Mejora esperada: $${mejora.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`);
      } else {
        toast.info(res.data.message || 'No se aplicaron modificaciones');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error aplicando optimización';
      toast.error(detail);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="advanced-features-page">
      <div className="hidden print:block mb-6 border-b-2 border-[#10B981] pb-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-gray-500">Reporte generado el {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          <p className="text-xs text-gray-500">cashflow.taxnfin.com</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="w-full">
          <PageHeader title="IA Ejecutiva" subtitle="Inteligencia artificial aplicada a tus finanzas" />
        </div>
        <Button variant="outline" size="sm" onClick={exportPDF} className="gap-2 mt-1">
          <FileText size={16} />
          Exportar PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-[#10B981]" data-testid="card-predictive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#10B981] text-base">
              <Brain size={20} />
              Análisis Predictivo
            </CardTitle>
            <CardDescription className="text-xs">ML + GPT-5.2</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={runAnalysis} disabled={loading} className="w-full bg-[#10B981]" data-testid="run-analysis-btn" size="sm">
              {loading ? 'Analizando...' : 'Ejecutar'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#F59E0B]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#F59E0B] text-base">
              <Zap size={20} />
              Auto-Conciliación
            </CardTitle>
            <CardDescription className="text-xs">Matching inteligente</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={runAutoRecon} className="w-full bg-[#F59E0B]" data-testid="run-recon-btn" size="sm">
              Ejecutar
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#EF4444]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#EF4444] text-base">
              <Bell size={20} />
              Alertas
            </CardTitle>
            <CardDescription className="text-xs">Monitoreo automático</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={checkAlerts} className="w-full bg-[#EF4444]" data-testid="check-alerts-btn" size="sm">
              Verificar
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#8B5CF6]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#8B5CF6] text-base">
              <GitBranch size={20} />
              Escenarios
            </CardTitle>
            <CardDescription className="text-xs">Qué pasaría si</CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={scenarioDialog} onOpenChange={setScenarioDialog}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#8B5CF6]" data-testid="create-scenario-btn" size="sm">
                  Crear
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuevo Escenario</DialogTitle>
                  <DialogDescription>Simula cambios y ve el impacto en tu flujo</DialogDescription>
                </DialogHeader>
                <div>
                  <p className="text-xs font-semibold text-[#64748B] mb-2">Elige un escenario rápido:</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {ESCENARIOS_RAPIDOS.map((e) => (
                      <Button
                        key={e.label}
                        variant="outline"
                        size="sm"
                        className="justify-start gap-2 text-xs h-9"
                        onClick={() => setScenarioForm({ ...scenarioForm, nombre: e.nombre, descripcion: e.descripcion, tipo: e.tipo })}
                      >
                        <span>{e.icono}</span>{e.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Nombre del Escenario</Label>
                    <Input value={scenarioForm.nombre} onChange={(e) => setScenarioForm({...scenarioForm, nombre: e.target.value})} placeholder="Ej: Adelantar pago proveedor" />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea value={scenarioForm.descripcion} onChange={(e) => setScenarioForm({...scenarioForm, descripcion: e.target.value})} placeholder="Describe el escenario..." />
                  </div>
                  <div>
                    <Label>Tipo de Modificación</Label>
                    <Select value={scenarioForm.tipo} onValueChange={(v) => setScenarioForm({...scenarioForm, tipo: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adelantar_pago">Adelantar Pago</SelectItem>
                        <SelectItem value="retrasar_cobro">Retrasar Cobro</SelectItem>
                        <SelectItem value="ajustar_monto">Ajustar Monto</SelectItem>
                        <SelectItem value="agregar_transaccion">Agregar Transacción</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createScenario}>Crear Escenario</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        <Card className="border-[#EC4899] bg-gradient-to-br from-pink-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#EC4899] text-base">
              <Cpu size={20} />
              CFO Virtual
            </CardTitle>
            <CardDescription className="text-xs">Algoritmos Genéticos</CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={optimizationDialog} onOpenChange={setOptimizationDialog}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#EC4899]" data-testid="optimize-genetic-btn" size="sm">
                  Optimizar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Cpu className="text-[#EC4899]" />
                    Optimización Genética
                  </DialogTitle>
                  <DialogDescription>
                    El algoritmo evaluará miles de combinaciones para encontrar la solución óptima
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 pt-1">
                  <span className="text-xs text-gray-500 self-center">Ejemplos:</span>
                  {[
                    { label: '⚡ Rápido',    cfg: { generaciones: 10, poblacion: 20, max_retraso_dias: 15, max_adelanto_dias: 7  } },
                    { label: '⚖️ Estándar',  cfg: { generaciones: 20, poblacion: 30, max_retraso_dias: 30, max_adelanto_dias: 15 } },
                    { label: '🔬 Profundo',  cfg: { generaciones: 50, poblacion: 80, max_retraso_dias: 45, max_adelanto_dias: 30 } },
                  ].map(({ label, cfg }) => (
                    <Button key={label} variant="outline" size="sm" className="text-xs h-7 px-2"
                      onClick={() => setOptimizationConfig(cfg)}>
                      {label}
                    </Button>
                  ))}
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Generaciones</Label>
                    <Input 
                      type="number" 
                      value={optimizationConfig.generaciones} 
                      onChange={(e) => setOptimizationConfig({...optimizationConfig, generaciones: parseInt(e.target.value)})} 
                    />
                    <p className="text-xs text-gray-500 mt-1">Más generaciones = mejor resultado (más tiempo)</p>
                  </div>
                  <div>
                    <Label>Tamaño Población</Label>
                    <Input 
                      type="number" 
                      value={optimizationConfig.poblacion} 
                      onChange={(e) => setOptimizationConfig({...optimizationConfig, poblacion: parseInt(e.target.value)})} 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Max Retraso (días)</Label>
                      <Input 
                        type="number" 
                        value={optimizationConfig.max_retraso_dias} 
                        onChange={(e) => setOptimizationConfig({...optimizationConfig, max_retraso_dias: parseInt(e.target.value)})} 
                      />
                    </div>
                    <div>
                      <Label>Max Adelanto (días)</Label>
                      <Input 
                        type="number" 
                        value={optimizationConfig.max_adelanto_dias} 
                        onChange={(e) => setOptimizationConfig({...optimizationConfig, max_adelanto_dias: parseInt(e.target.value)})} 
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={runGeneticOptimization} disabled={loading} data-testid="run-genetic-opt">
                    {loading ? 'Optimizando...' : 'Iniciar Optimización'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {scenarioResult && (
        <Card className="border-[#10B981] bg-[#F0FDF4]" data-testid="scenario-result">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-[#10B981] text-base">Resultado: {scenarioResult.nombre}</CardTitle>
              <button onClick={() => setScenarioResult(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="p-3 bg-white rounded border">
                <p className="text-xs text-[#64748B] mb-1">Flujo Neto — Baseline</p>
                <p className="text-xl font-bold mono text-[#0F172A]">${(scenarioResult.flujo_neto_baseline ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="p-3 bg-white rounded border">
                <p className="text-xs text-[#64748B] mb-1">Flujo Neto — Simulado</p>
                <p className="text-xl font-bold mono text-[#10B981]">${(scenarioResult.flujo_neto_simulado ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded border">
              <div className="shrink-0">
                <p className="text-xs text-[#64748B]">Diferencia</p>
                <p className={`text-lg font-bold mono ${(scenarioResult.comparison?.diferencia_flujo_neto ?? 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {(scenarioResult.comparison?.diferencia_flujo_neto ?? 0) >= 0 ? '+' : ''}${(scenarioResult.comparison?.diferencia_flujo_neto ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <p className="text-sm font-medium text-[#0F172A]">{scenarioResult.comparison?.recomendacion}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {analysis && (
        <Card className="border-[#10B981]" data-testid="analysis-results">
          <CardHeader>
            <CardTitle>Resultados del Análisis Predictivo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-[#F0FDF4] rounded">
                <p className="text-sm text-[#166534]">Ingreso Promedio Semanal</p>
                <p className="text-2xl font-bold mono text-[#10B981]">${analysis.analisis_cuantitativo.ingresos_promedio_semanal.toLocaleString('es-MX')}</p>
              </div>
              <div className="p-4 bg-[#FEF2F2] rounded">
                <p className="text-sm text-[#991B1B]">Egreso Promedio Semanal</p>
                <p className="text-2xl font-bold mono text-[#EF4444]">${analysis.analisis_cuantitativo.egresos_promedio_semanal.toLocaleString('es-MX')}</p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded">
                <p className="text-sm text-[#64748B]">Flujo Neto Promedio</p>
                <p className="text-2xl font-bold mono text-[#0F172A]">${analysis.analisis_cuantitativo.flujo_neto_promedio.toLocaleString('es-MX')}</p>
              </div>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded">
              <h3 className="font-semibold mb-2">Predicciones (8 semanas):</h3>
              {analysis.predicciones_8_semanas.slice(0, 4).map(pred => (
                <div key={pred.semana_futura} className="text-sm mb-1">
                  S+{pred.semana_futura}: Neto ${pred.flujo_neto_predicho.toLocaleString()} ({pred.confianza})
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-white border rounded">
              <h3 className="font-semibold mb-2">Insights IA:</h3>
              <div className="text-sm whitespace-pre-wrap">{analysis.insights_ia}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {reconResult && (
        <Card className="border-[#F59E0B]">
          <CardHeader>
            <CardTitle>Resultado Auto-Conciliación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold mono text-[#10B981]">{reconResult.reconciled}</p>
                <p className="text-sm text-[#64748B]">Conciliados</p>
              </div>
              <div>
                <p className="text-3xl font-bold mono text-[#F59E0B]">{reconResult.skipped}</p>
                <p className="text-sm text-[#64748B]">Omitidos</p>
              </div>
              <div>
                <p className="text-3xl font-bold mono text-[#64748B]">{reconResult.total_processed}</p>
                <p className="text-sm text-[#64748B]">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {optimizationResult?._no_improvement && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-5 pb-4 flex items-start gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <p className="font-semibold text-amber-800 mb-1">Sin mejoras detectadas</p>
              <p className="text-sm text-amber-700 mb-3">El algoritmo corrió correctamente pero ninguna combinación mejoró tu flujo actual. Prueba con el preset 🔬 Profundo o agrega más proyecciones de cobros futuros.</p>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => { setOptimizationResult(null); setOptimizationDialog(true); }}>
                Reintentar con más generaciones
              </Button>
            </div>
            <button onClick={() => setOptimizationResult(null)} className="ml-auto text-amber-400 hover:text-amber-600 text-lg leading-none">✕</button>
          </CardContent>
        </Card>
      )}

      {optimizationResult?._insufficient_data && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-5 pb-4 flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <p className="font-semibold text-amber-800 mb-1">Sin proyecciones para optimizar</p>
              <p className="text-sm text-amber-700 mb-3">No hay transacciones proyectadas para optimizar. Ve al Cash Flow y agrega proyecciones para las próximas semanas, luego vuelve aquí.</p>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => navigate('/projections')}>
                Ir al Cash Flow →
              </Button>
            </div>
            <button onClick={() => setOptimizationResult(null)} className="ml-auto text-amber-400 hover:text-amber-600 text-lg leading-none">✕</button>
          </CardContent>
        </Card>
      )}

      {optimizationResult && !optimizationResult._insufficient_data &&
        (optimizationResult.mejora_vs_baseline?.flujo_neto ?? 0) <= 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-5 pb-4">
            <p className="font-semibold text-amber-800">⚠️ Sin mejoras encontradas</p>
            <p className="text-sm text-amber-700 mt-1">
              El algoritmo no encontró combinaciones que mejoren tu flujo actual.
              Agrega más proyecciones de cobros y pagos futuros en el Cash Flow para obtener mejores resultados.
            </p>
          </CardContent>
        </Card>
      )}

      {optimizationResult && !optimizationResult._insufficient_data && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          💡 Tu CFO Virtual analizó <strong>{optimizationResult.n_cxc || 0} facturas por cobrar</strong> y <strong>{optimizationResult.n_cxp || 0} pagos pendientes</strong>.{' '}
          Encontró que reorganizando fechas de cobro y pago puedes mejorar tu liquidez en{' '}
          <strong>${(optimizationResult.mejora_vs_baseline?.flujo_neto || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong> pesos{' '}
          en las próximas semanas, sin cambiar ningún contrato.
        </div>
      )}
      {optimizationResult && !optimizationResult._insufficient_data && (
        <Card className="border-[#EC4899] bg-gradient-to-br from-pink-50 to-white" data-testid="optimization-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#EC4899]">
              <Cpu size={24} />
              Optimización Genética Completada
            </CardTitle>
            <CardDescription>
              {optimizationResult.generaciones} generaciones • {optimizationResult.top_5_soluciones?.length || 0} soluciones óptimas encontradas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-[#BE185D]">🏆 Mejor Solución</p>
                  <Badge className="bg-[#EC4899] mt-1">Rank #1</Badge>
                </div>
                <CheckCircle className="text-[#10B981]" size={32} />
              </div>
              
              {(() => {
                const mejora = optimizationResult.mejora_vs_baseline?.flujo_neto ?? 0;
                const mejoraColor = mejora >= 0 ? '#10B981' : '#EF4444';
                const mejoraTexto = mejora >= 0
                  ? `+$${mejora.toLocaleString('es-MX')}`
                  : `-$${Math.abs(mejora).toLocaleString('es-MX')}`;
                return (
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-[#BE185D]">Mejora en Flujo Neto</p>
                      <p className="text-2xl font-bold mono" style={{ color: mejoraColor }}>{mejoraTexto}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#BE185D]">Crisis Evitadas</p>
                      <p className="text-2xl font-bold mono text-[#10B981]">
                        {optimizationResult.mejora_vs_baseline?.semanas_criticas_resueltas ?? 0} semanas
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-2">
                <p className="text-sm font-semibold text-[#BE185D]">Modificaciones Sugeridas:</p>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {optimizationResult.mejor_solucion?.modificaciones?.slice(0, 5).map((mod, idx) => {
                    const limpiarConcepto = (raw) => {
                      if (!raw) return 'transacción';
                      return raw
                        .replace(/^cache_(ingreso|egreso)_/i, '')
                        .replace(/_\d{4}-\d{2}-\d{2}$/, '')
                        .trim() || 'transacción';
                    };
                    const traducirModificacion = (mod) => {
                      const monto = mod.monto_original
                        ? `$${parseFloat(mod.monto_original).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`
                        : '';
                      const rawId = mod.concepto || mod.transaction_id || '';
                      const concepto = limpiarConcepto(rawId);
                      const esIngreso = rawId.toLowerCase().startsWith('cache_ingreso');
                      switch (mod.tipo) {
                        case 'retrasar_pago':
                          return `⏳ Retrasa el pago de ${concepto} ${monto} — pídele ${mod.dias || mod.nuevo_valor || ''} días más de plazo a tu proveedor`;
                        case 'adelantar_cobro':
                          return `⚡ Adelanta el cobro de ${concepto} ${monto} — cobra antes a este cliente`;
                        case 'ajustar_monto': {
                          if (esIngreso) {
                            return `⚡ Adelanta el cobro de ${concepto} ${monto}`;
                          }
                          const pct = mod.nuevo_valor ? Math.round((1 - mod.nuevo_valor) * 100) : '';
                          return `✂️ Reduce ${concepto} ${monto}${pct ? ` un ${pct}%` : ''} — negocia este gasto a la baja`;
                        }
                        case 'adelantar_pago':
                          return `📅 Adelanta el pago de ${concepto} — aprovecha descuento por pronto pago`;
                        case 'retrasar_cobro':
                          return `🔄 Ajusta el cobro de ${concepto} ${monto}`;
                        default:
                          return `🔧 ${mod.tipo?.replace(/_/g, ' ')}: ${concepto} ${monto}`;
                      }
                    };
                    return (
                      <div key={idx} className="p-2 bg-white rounded text-xs">
                        {traducirModificacion(mod)}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => applyOptimization(optimizationResult.optimization_id)}
                  className="bg-[#10B981] flex-1"
                  data-testid="apply-optimization-btn"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Aplicar Solución
                </Button>
                <Button 
                  onClick={() => setOptimizationResult(null)} 
                  variant="outline"
                >
                  Descartar
                </Button>
              </div>
            </div>

            {optimizationResult.top_5_soluciones && optimizationResult.top_5_soluciones.length > 1 && (
              <div>
                <p className="text-sm font-semibold mb-2">Otras Soluciones Óptimas:</p>
                <div className="space-y-2">
                  {optimizationResult.top_5_soluciones.slice(1, 4).map((sol) => (
                    <div key={sol.rank} className="p-3 border rounded hover:bg-gray-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <Badge variant="outline">Rank #{sol.rank}</Badge>
                          <p className="text-sm mt-1">Mejora: ${sol.mejora_flujo_neto.toLocaleString()}</p>
                        </div>
                        <p className="text-xs text-gray-500">{sol.modificaciones.length} cambios</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {appliedResult && (
        <Card className="border-[#10B981] bg-green-50" data-testid="applied-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#10B981]">
              <CheckCircle size={20} />
              Recomendaciones del CFO Virtual aplicadas
            </CardTitle>
            <CardDescription>
              {appliedResult.modificaciones_aplicadas} modificaciones · Mejora esperada:{' '}
              ${(appliedResult.mejora_esperada || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(appliedResult.recomendaciones || []).map((rec, idx) => {
                const traducirRecomendacion = (rec) => {
                  const nombre = (rec.transaction_id || '')
                    .replace(/^cache_(ingreso|egreso)_/i, '')
                    .replace(/_\d{4}-\d{2}-\d{2}$/, '')
                    .trim();
                  switch (rec.tipo) {
                    case 'retrasar_pago': {
                      const dias = rec.recomendacion?.match(/(\d+)\s*días/)?.[1] || '';
                      return `Negocia con ${nombre} para pagar ${dias} días después — mejora tu liquidez sin afectar la relación comercial`;
                    }
                    case 'adelantar_cobro': {
                      const dias = rec.recomendacion?.match(/(\d+)\s*días/)?.[1] || '';
                      return `Contacta a ${nombre} para cobrar ${dias} días antes — prioriza este cliente en tu gestión de cobranza`;
                    }
                    case 'adelantar_pago': {
                      const dias = rec.recomendacion?.match(/(\d+)\s*días/)?.[1] || '';
                      return `Paga a ${nombre} ${dias} días antes — verifica si ofrece descuento por pronto pago`;
                    }
                    case 'retrasar_cobro': {
                      const dias = rec.recomendacion?.match(/(\d+)\s*días/)?.[1] || '';
                      return `Ajusta el cobro a ${nombre} en ${dias} días — coordina con tu área comercial`;
                    }
                    case 'ajustar_monto': {
                      const pct = rec.recomendacion?.match(/([\d.]+)%/)?.[1];
                      const pctNum = pct ? parseFloat(pct) : null;
                      if (pctNum && pctNum < 100) {
                        return `Negocia una reducción del ${(100 - pctNum).toFixed(0)}% con ${nombre} — busca descuento o servicio alternativo más económico`;
                      } else if (pctNum && pctNum > 100) {
                        return `Gestiona cobrar un ${(pctNum - 100).toFixed(0)}% adicional a ${nombre} — revisa si hay conceptos pendientes`;
                      }
                      return `Ajusta el monto con ${nombre}`;
                    }
                    default:
                      return rec.recomendacion || rec.tipo;
                  }
                };
                return (
                  <div key={idx} className="p-3 bg-white border border-green-200 rounded text-sm">
                    <span className="font-semibold text-[#10B981]">
                      {rec.tipo === 'retrasar_pago' ? '⏳' :
                       rec.tipo === 'adelantar_cobro' ? '⚡' :
                       rec.tipo === 'ajustar_monto' ? '✂️' : '🔧'}{' '}
                      {rec.tipo?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-gray-600 ml-2">
                      {(rec.transaction_id || '')
                        .replace(/^cache_(ingreso|egreso)_/i, '')
                        .replace(/_\d{4}-\d{2}-\d{2}$/, '')}
                    </span>
                    <p className="text-gray-600 mt-1">{traducirRecomendacion(rec)}</p>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => setAppliedResult(null)}
              className="mt-3 text-xs text-gray-400 hover:text-gray-600"
            >
              Cerrar
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedFeatures;
